import React, { useMemo, useState } from "react";
import "../../../css/calculators/MortgageBorrow.css";
import SecondaryButton from "../Button";
import PrimaryButton from "../Button";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import CustomSelect from "../CustomSelect";

const initialState = {
  propertyValue: "",
  youngestAge: "55",
};

const ageOptions = [
  { value: "55", label: "55" },
  { value: "56", label: "56" },
  { value: "57", label: "57" },
  { value: "58", label: "58" },
  { value: "59", label: "59" },
  { value: "60", label: "60" },
  { value: "61", label: "61" },
  { value: "62", label: "62" },
  { value: "63", label: "63" },
  { value: "64", label: "64" },
  { value: "65", label: "65" },
  { value: "66", label: "66" },
  { value: "67", label: "67" },
  { value: "68", label: "68" },
  { value: "69", label: "69" },
  { value: "70", label: "70" },
  { value: "71", label: "71" },
  { value: "72", label: "72" },
  { value: "73", label: "73" },
  { value: "74", label: "74" },
  { value: "75", label: "75" },
  { value: "76", label: "76" },
  { value: "77", label: "77" },
  { value: "78", label: "78" },
  { value: "79", label: "79" },
  { value: "80", label: "80" },
  { value: "81", label: "81" },
  { value: "82", label: "82" },
  { value: "83", label: "83" },
  { value: "84", label: "84" },
  { value: "85", label: "85" },
  { value: "86", label: "86" },
  { value: "87", label: "87" },
  { value: "88", label: "88" },
  { value: "89", label: "89" },
  { value: "90", label: "90" },
];

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatNumber = (value) => {
  return Number(value || 0).toLocaleString("en-GB");
};

// Age-based release percentages.
// You can adjust these easily if you want different lender assumptions.
const getReleasePercentage = (age) => {
  const ageNumber = Number(age);

//   if (ageNumber >= 90) return 0.55;
//   if (ageNumber >= 85) return 0.58;
  if (ageNumber >= 80) return 0.58;
  if (ageNumber >= 75) return 0.53;
  if (ageNumber >= 70) return 0.48;
  if (ageNumber >= 65) return 0.43;
  if (ageNumber >= 60) return 0.38;
  return 0.31; // 55-59
};

export default function EquityReleaseCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [hasCalculated, setHasCalculated] = useState(false);

 const propertyValueNumber = useMemo(() => {
   return Number(formData.propertyValue || 0);
 }, [formData.propertyValue]);

  const releasePercentage = useMemo(() => {
    return getReleasePercentage(formData.youngestAge);
  }, [formData.youngestAge]);

  const maxReleasePlan = useMemo(() => {
    if (!hasCalculated || propertyValueNumber < 70000) return 0;
    return Math.round(propertyValueNumber * releasePercentage);
  }, [hasCalculated, propertyValueNumber, releasePercentage]);

  const medicallyEnhancedPlan = useMemo(() => {
    if (!hasCalculated || propertyValueNumber < 70000) return 0;
    return Math.round(maxReleasePlan * 1.4065);
  }, [hasCalculated, propertyValueNumber, maxReleasePlan]);

 const handleChange = (e) => {
   const { name, value } = e.target;

   setFormData((prev) => ({
     ...prev,
     [name]: value,
   }));
 };
  const validateForm = () => {
    const newErrors = {};

    if (!propertyValueNumber) {
      newErrors.propertyValue = "Estimated property value is required";
    } else if (propertyValueNumber <= 70000) {
      newErrors.propertyValue = `Estimated property value : ${formatCurrency(
        propertyValueNumber,
      )} must be greater than £70,000!`;
    }

    if (!formData.youngestAge) {
      newErrors.youngestAge = "Please select the age of the youngest";
    }

    return newErrors;
  };

  const handleCalculate = (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setHasCalculated(false);
      return;
    }

    setHasCalculated(true);
  };

  const handleClear = () => {
    setFormData(initialState);
    setErrors({});
    setHasCalculated(false);
  };

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="borrow-form-card">
          <div className="title primary">Your details</div>

          {errors.propertyValue && (
            <div className="borrow-error-box">
              <div className="borrow-error-title">Error!</div>
              <ul className="borrow-error-list">
                <li>{errors.propertyValue}</li>
              </ul>
            </div>
          )}

          <form onSubmit={handleCalculate}>
            <div className="borrow-field-group">
              <label className="borrow-label">
                Estimated property value (£)
              </label>
              <FormattedNumberInput
                name="propertyValue"
                value={formData.propertyValue}
                onChange={(raw) => {
                  setFormData((prev) => ({
                    ...prev,
                    propertyValue: raw,
                  }));

                  setErrors((prev) => ({
                    ...prev,
                    propertyValue: "",
                  }));
                }}
                className="borrow-input"
                placeholder="Enter estimated property value"
              />
              {errors.propertyValue && (
                <div className="borrow-field-error">
                  Estimated property value must be greater than £70,000
                </div>
              )}
            </div>

            <div className="borrow-field-group">
              <label className="borrow-label">Age of the youngest</label>
              {/* <select
                name="youngestAge"
                value={formData.youngestAge}
                onChange={handleChange}
                className="borrow-input"
              >
                {ageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              <CustomSelect
                options={ageOptions}
                value={formData.youngestAge}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    youngestAge: value,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    youngestAge: "",
                  }));
                }}
                className="borrow-custom-select"
              />
              {errors.youngestAge && (
                <div className="borrow-field-error">{errors.youngestAge}</div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 16,
                marginTop: 24,
                justifyContent: "flex-end",
              }}
            >
              <SecondaryButton
                onClick={handleClear}
                text="Clear"
                variant="secondary"
              />
              <PrimaryButton
                onClick={handleCalculate}
                text="Calculate"
                variant="primary"
              />
            </div>
          </form>

          <div className="borrow-divider" />

          {hasCalculated && (
            <div className="borrow-result-wrap result-container">
              <div className="title">Result</div>

              <div className="borrow-result-item">
                <div className="content-semibold">Maximum release plan</div>
                <div className="title-semibold">
                  {formatCurrency(maxReleasePlan)}
                </div>
              </div>

              <div className="borrow-result-item">
                <div className="content-semibold">Medically enhanced plan</div>
                <div className="title-semibold">
                  {formatCurrency(medicallyEnhancedPlan)}
                </div>
              </div>

              <div className="content">
                <strong>Important!</strong> You will need to repay any existing
                mortgages on your property with the funds from your equity
                release plan. After the charges against your property have been
                settled, you will have the remaining funds to spend as you wish.
              </div>

              <div className="result-divider" />
              <div className="result-note">
                <div className="content-semibold">PLEASE NOTE:</div>
                <p className="content">
                  This equity release calculator is an interactive tool which
                  should be used for your guidance only, and must not form part
                  of your financial decision making process.
                </p>
                <p className="content">
                  Equity Release will reduce the value of your estate and can
                  affect your eligibility for means tested benefits.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
